<script setup lang="ts">
import { VIcon } from "@app/core";
import { useTemplateRef } from "vue";

const query = defineModel<string>({ required: true });

const { isOpen } = defineProps<{
  isOpen: boolean
}>();

const emit = defineEmits<{
  keydown: [e: KeyboardEvent]
  clear: []
}>();

const inputRef = useTemplateRef<HTMLInputElement>("input");

function focus() {
  inputRef.value?.focus();
}

function clear(e: MouseEvent) {
  e.stopPropagation();
  emit("clear");
  inputRef.value?.focus();
}

defineExpose({ focus });
</script>

<template>
  <div
    :class="{ 'search-trigger--open': isOpen }"
    class="search-trigger"
    @click="focus"
  >
    <VIcon
      class="search-trigger__icon"
      icon="lucide:search"
      :size="17"
    />

    <input
      ref="input"
      v-model="query"
      autocomplete="off"
      class="search-trigger__input"
      placeholder="Search products..."
      spellcheck="false"
      type="search"
      @keydown="emit('keydown', $event)"
    >

    <button
      v-if="query"
      aria-label="Clear search"
      class="search-trigger__clear"
      tabindex="-1"
      type="button"
      @click="clear"
    >
      <VIcon
        icon="lucide:x"
        :size="14"
      />
    </button>

    <kbd
      v-else
      class="search-trigger__kbd"
    >
      <span class="hidden sm:inline">⌘K</span>
    </kbd>
  </div>
</template>

<style scoped>
.search-trigger {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  height: 2.625rem;
  padding: 0 0.875rem;
  background-color: rgb(var(--color-cardBg, 255 255 255) / 1);
  border: 1.5px solid rgb(var(--color-base-300, 226 232 240) / 1);
  border-radius: 0.75rem;
  cursor: text;
  transition:
    border-color 200ms cubic-bezier(0.23, 1, 0.32, 1),
    box-shadow 200ms cubic-bezier(0.23, 1, 0.32, 1);
}

@media (hover: hover) and (pointer: fine) {
  .search-trigger:hover {
    border-color: rgb(var(--color-primary, 14 165 233) / 0.4);
  }
}

.search-trigger--open,
.search-trigger:focus-within {
  border-color: rgb(var(--color-primary, 14 165 233) / 0.7);
  box-shadow: 0 0 0 3px rgb(var(--color-primary, 14 165 233) / 0.1);
}

.search-trigger__icon {
  color: rgb(var(--color-secondaryText, 100 116 139) / 1);
  flex-shrink: 0;
  transition: color 200ms ease;
}

.search-trigger:focus-within .search-trigger__icon {
  color: rgb(var(--color-primary, 14 165 233) / 1);
}

.search-trigger__input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.875rem;
  color: rgb(var(--color-mainText, 30 41 59) / 1);
  font-family: inherit;
}

.search-trigger__input::placeholder {
  color: rgb(var(--color-secondaryText, 100 116 139) / 0.7);
}

/* hide native search clear button across browsers */
.search-trigger__input::-webkit-search-cancel-button,
.search-trigger__input::-webkit-search-decoration {
  -webkit-appearance: none;
  appearance: none;
}

.search-trigger__clear {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.375rem;
  height: 1.375rem;
  border-radius: 0.25rem;
  border: none;
  background: transparent;
  color: rgb(var(--color-secondaryText, 100 116 139) / 1);
  cursor: pointer;
  flex-shrink: 0;
  transition: color 100ms ease, background-color 100ms ease;
}

.search-trigger__clear:hover {
  color: rgb(var(--color-error, 239 68 68) / 1);
  background-color: rgb(var(--color-error, 239 68 68) / 0.08);
}

.search-trigger__kbd {
  display: flex;
  align-items: center;
  gap: 0.125rem;
  padding: 0.125rem 0.375rem;
  background-color: rgb(var(--color-base-200, 241 245 249) / 1);
  border: 1px solid rgb(var(--color-base-300, 226 232 240) / 1);
  border-radius: 0.25rem;
  font-family: ui-monospace, monospace;
  font-size: 0.6875rem;
  color: rgb(var(--color-secondaryText, 100 116 139) / 1);
  white-space: nowrap;
  pointer-events: none;
  flex-shrink: 0;
  user-select: none;
}
</style>
